import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, CircularProgress,
} from '@mui/material';

import { purchaseOrderService } from '../../services/purchaseOrderService';
import { useLanguage } from '../../contexts/LanguageContext';
import OrderDetailView from '../OrderDetailView';

/**
 * Le détail d'une commande, en DIALOGUE par-dessus la liste.
 *
 * 🔴 POURQUOI UN COMPOSANT ET NON UN `<Dialog>` DANS CHAQUE ÉCRAN. Le détail
 * s'ouvre depuis TROIS écrans — Commandes, Réception, Comptabilité. Trois
 * enveloppes recopiées, c'est trois `maxWidth`, trois boutons « Fermer » et
 * trois chargements qui divergeront au premier correctif appliqué à un seul.
 * C'est exactement le défaut mesuré sur le dialogue « Ajouter un paiement »,
 * écrit deux fois et déjà divergent (cf. RU4-d au ROADMAP) : on ne le recrée
 * pas ici en connaissance de cause.
 *
 * 🔴 L'OUVERTURE EST DÉRIVÉE DE L'URL, JAMAIS D'UN `useState`. C'est ce qui
 * rend le bouton « précédent » du navigateur actif sans une ligne d'écoute de
 * l'historique : revenir en arrière change l'URL, donc `ouvert` devient faux,
 * donc le dialogue se ferme — au lieu de quitter l'écran. Un état local
 * survivrait à la navigation et laisserait le dialogue ouvert sur une URL qui
 * dit le contraire. *(Motif établi au lot RU3-d′ sur la fiche produit.)*
 *
 * ⚠️ ET L'URL EST PROPRE À CHAQUE ÉCRAN — `/purchase-orders/:id`,
 * `/order-reception/:id`, `/accounting/:id`. Une URL unique partagée par les
 * trois ferait SORTIR de l'écran courant : ouvrir le détail depuis la
 * Comptabilité déposerait le lecteur sur Commandes en refermant. Le chemin dit
 * d'où l'on regarde, pas seulement ce qu'on regarde.
 *
 * `id` vient de l'URL, donc c'est une CHAÎNE : la commande est chargée ici, et
 * non reçue toute faite. C'est ce qui fait qu'un lien collé dans un onglet neuf
 * affiche la commande au lieu d'un écran vide.
 */
const DialogueDetailCommande = ({
  id, ouvert, onFermer, contexte, showSnackbar, onCommandeModifiee,
}) => {
  const { t } = useLanguage();
  const [commande, setCommande] = useState(null);
  const [chargement, setChargement] = useState(false);

  const charger = async () => {
    if (!ouvert || !id) return;
    try {
      setChargement(true);
      setCommande(await purchaseOrderService.getPurchaseOrderById(id));
    } catch (err) {
      console.error('Erreur lors du chargement de la commande:', err);
      if (showSnackbar) showSnackbar(t('messages.detailsLoadError'), 'error');
      onFermer();
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ouvert]);

  // Le panneau se recharge après une action qui l'a modifié — réception,
  // paiement, annulation — ET la liste dessous en est prévenue : les deux
  // affichent le même statut, et l'une des deux ne doit pas rester en retard.
  const apresModification = async () => {
    await charger();
    if (onCommandeModifiee) onCommandeModifiee();
  };

  return (
    <Dialog open={ouvert} onClose={onFermer} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        {chargement || !commande ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <OrderDetailView
            order={commande}
            contexte={contexte}
            showSnackbar={showSnackbar}
            onOrderUpdated={apresModification}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogueDetailCommande;
